/**
 * Que se note que la aplicación recibió el clic.
 *
 * ## El problema
 *
 * Un enlace que navega no da ninguna señal hasta que llega la página nueva. En
 * local eso son cincuenta milisegundos y no se ve; en un hosting compartido con
 * la sesión de Owari de por medio son uno o dos segundos de pantalla idéntica.
 * Quien pulsa no sabe si le hizo caso, así que pulsa otra vez.
 *
 * {@link setupFormGuards} ya cubre los formularios -deshabilita el botón y le
 * pone el spinner-. Lo que faltaba son los **enlaces**: «Nuevo evento»,
 * «Atrás», el botón que lleva a otra pantalla. Son la mayoría de los clics.
 *
 * ## Dos señales, no una
 *
 * **La barra de arriba** dice que la aplicación está trabajando. Es global y no
 * hace falta tocar ninguna plantilla para tenerla.
 *
 * **El control pulsado** se marca ocupado: si era un botón, le sale el spinner
 * dentro. Sin esto, en una pantalla con seis botones la barra dice que algo
 * pasa pero no cuál de los seis se pulsó.
 *
 * ## Qué NO dispara la señal
 *
 * Un enlace que no navega -abre en otra pestaña, baja a un ancla, descarga un
 * fichero, lo abrió con Ctrl- dejaría la barra encendida para siempre, porque
 * nunca llega una página que la apague. Se descartan todos abajo.
 *
 * ## La vuelta atrás del navegador
 *
 * Volver con el botón «atrás» restaura la página desde la caché tal como
 * estaba, **con la barra encendida y el botón bloqueado**. Por eso se escucha
 * `pageshow`: es el único aviso de que la página revivió en vez de cargarse.
 */

const MARCA = '__companyNavProgress';
const CLASE_BARRA = 'company-progress';
const CLASE_OCUPADO = 'company-busy';

let barra = null;
let temporizador = null;

/**
 * Cuánto se espera antes de rendirse y devolver la pantalla.
 *
 * Normalmente no llega a dispararse: la página se va antes. Está para cuando la
 * navegación no ocurre -el servidor no contesta, o algo canceló el clic-, y
 * quince segundos es más que cualquier carga normal y menos que la paciencia de
 * quien mira.
 */
const RESPALDO = 15000;

function laBarra() {
  if (barra && barra.isConnected) return barra;

  barra = document.createElement('div');
  barra.className = CLASE_BARRA;
  barra.setAttribute('role', 'presentation');
  document.body.appendChild(barra);

  return barra;
}

function arrancar(control) {
  const b = laBarra();

  // Reiniciar la animación: sin esto, un segundo clic antes de que llegue la
  // página deja la barra donde estaba y parece congelada.
  b.classList.remove('is-active');
  void b.offsetWidth;
  b.classList.add('is-active');

  if (control && !control.classList.contains(CLASE_OCUPADO)) {
    control.classList.add(CLASE_OCUPADO);

    // El spinner solo en botones y enlaces con pinta de botón. En un enlace de
    // texto corriente metería un circulito en mitad de una frase.
    if (control.matches('.btn')) {
      const spinner = document.createElement('span');
      spinner.className = 'spinner-border spinner-border-sm company-btn-spinner';
      spinner.setAttribute('role', 'status');
      spinner.setAttribute('aria-hidden', 'true');
      control.prepend(spinner);
    }
  }

  clearTimeout(temporizador);
  temporizador = setTimeout(parar, RESPALDO);
}

function parar() {
  clearTimeout(temporizador);

  if (barra) barra.classList.remove('is-active');

  document.querySelectorAll(`.${CLASE_OCUPADO}`).forEach((control) => {
    control.classList.remove(CLASE_OCUPADO);
    control.querySelectorAll('.company-btn-spinner').forEach((s) => s.remove());
  });
}

/** ¿Este clic va a traer una página nueva? */
function navega(enlace, evento) {
  // Con Ctrl, Cmd, Shift o el botón central se abre aparte: esta pestaña se
  // queda donde está.
  if (evento.metaKey || evento.ctrlKey || evento.shiftKey || evento.altKey) return false;
  if (evento.button !== undefined && evento.button !== 0) return false;
  if (evento.defaultPrevented) return false;

  const destino = enlace.getAttribute('href') || '';

  if (destino === '' || destino.startsWith('#')) return false;
  if (destino.startsWith('javascript:')) return false;
  // `mailto:`, `tel:`, `whatsapp:`… abren otra cosa y esta página se queda.
  if (/^[a-z][a-z0-9+.-]*:/i.test(destino) && !/^https?:/i.test(destino)) return false;

  if (enlace.hasAttribute('download')) return false;
  if (enlace.target && enlace.target !== '_self') return false;
  // Lo que se marque a mano: un enlace que abre un diálogo o dispara un guion
  // propio no navega, y nadie más lo sabe.
  if (enlace.hasAttribute('data-company-no-progress')) return false;

  // Otro dominio también navega, así que cuenta. Solo se descarta el ancla
  // dentro de la misma página, que cambia el hash y no recarga nada.
  try {
    const url = new URL(destino, window.location.href);
    if (url.origin === window.location.origin
      && url.pathname === window.location.pathname
      && url.search === window.location.search
      && url.hash !== '') {
      return false;
    }
  } catch {
    return false;
  }

  return true;
}

export function setupNavProgress(root = document) {
  const alcance = root === document ? document : root;
  if (alcance[MARCA]) return;
  alcance[MARCA] = true;

  // Delegado en el documento y no enlace por enlace: así lo que se pinte
  // después -una tabla que se repagina, un diálogo- queda cubierto sin que
  // nadie tenga que acordarse de volver a montar nada.
  document.addEventListener('click', (evento) => {
    const enlace = evento.target.closest('a[href]');
    if (enlace && navega(enlace, evento)) {
      arrancar(enlace);
      return;
    }

    // Botones que no envían un formulario pero llevan a otro sitio por su
    // cuenta. Se marcan con `data-company-nav`.
    const boton = evento.target.closest('[data-company-nav]');
    if (boton) arrancar(boton);
  });

  // Los formularios: `form-guard` se ocupa del botón, pero la barra la pone
  // esto para que la señal sea la misma se pulse lo que se pulse.
  document.addEventListener('submit', (evento) => {
    if (!evento.defaultPrevented) arrancar(null);
  });

  // Volver atrás restaura la página tal como se dejó, con la barra encendida.
  window.addEventListener('pageshow', parar);
}
